import React from "react";

import { Box, Flex } from "@chakra-ui/react";

import { ClassesEnum } from "@ts/enums/ClassesEnum";

import { getTrackingId } from "@utils/utils";

interface ItemType {
    id: number | string;
}

interface PropTypes<T> {
    children: React.ReactNode;
    bgColor?: string;
    isActive?: boolean;
    isClickable?: boolean;
    item: T;
    width?:
        | (number | string | null)[]
        | number
        | string
        | { [key: string]: number | string };
    height?:
        | (number | string | null)[]
        | number
        | string
        | { [key: string]: number | string };
    padding?: (number | string | null)[] | number | string;
    isHighlighted?: boolean;
    highlightedText?: string;
    cardBorderRadius?: number | string;
    cardBorder?: number | string | { [key: string]: number | string };
    highlightedTextAlign?: "end" | "center";
    onClick?: (i: T) => void;
    boxShadow?: { [key: string]: number | string };
    _last?:
        | {
              [key: string]: {
                  ".chakra-wrap__list": {
                      justifyContent: string;
                  };
                  ".chakra-wrap__listitem": {
                      width: string | number;
                  };
              };
          }
        | string;
    className?: string;
    action?: string;
    classHierarchy?: ClassesEnum[];
}

const Card = <T extends ItemType>({
    item,
    isActive = false,
    isClickable = true,
    children,
    width,
    bgColor = "white",
    height,
    padding,
    isHighlighted = false,
    highlightedText = "",
    cardBorderRadius,
    cardBorder = 2,
    highlightedTextAlign,
    boxShadow = { base: "0px 9px 18px -3px rgba(111,112,112,0.18)" },
    _last = "",
    className,
    action,
    classHierarchy,
    onClick
}: PropTypes<T>) => {
    return (
        <Flex
            className={className ?? ClassesEnum.Card}
            data-tracking-id={getTrackingId(
                classHierarchy ?? [ClassesEnum.Card],
                action ?? item.id ?? "selectCard"
            )}
            onClick={() => {
                if (typeof onClick === "function") {
                    onClick(item);
                }
            }}
            key={item.id}
            border="solid transparent"
            borderWidth={cardBorder}
            p={padding ?? [4, 8]}
            transition="all 0.2s"
            justify="center"
            // align={highlightedTextAlign ?? "center"}
            borderRadius={cardBorderRadius ?? 15}
            maxW={width ?? "100%"}
            width="100%"
            h={height ?? "auto"}
            direction="column"
            cursor={isClickable ? "pointer" : "default"}
            position="relative"
            background={{
                base: `linear-gradient(${bgColor} 0 0) padding-box , ${
                    isActive ? "#F0A32F" : "var(--euka-colors-gray-300)"
                } border-box`
            }}
            _hover={{
                boxShadow
            }}
            _last={{
                _last
            }}
        >
            {children}
            {isHighlighted && (
                <Box
                    position="absolute"
                    color="gray.800"
                    borderRadius={32}
                    top="-17px"
                    paddingX={8}
                    paddingY={2}
                    background="#F1D567"
                    fontWeight={700}
                    textShadow="base"
                    fontSize={12}
                    right={
                        highlightedTextAlign === "center"
                            ? "50%"
                            : { base: "50%", md: "2rem" }
                    }
                    transform={
                        highlightedTextAlign === "center"
                            ? "translateX(50%)"
                            : { base: "translateX(50%)", md: "translateX(0)" }
                    }
                >
                    {highlightedText}
                </Box>
            )}
        </Flex>
    );
};

export default Card;
